import { IconButton, Stack } from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  DosePrescription,
  SiteGroupName,
} from "@components/SiteTable/SiteTable.types";
import {
  useWebApiV1CreateSiteMutation,
  useWebApiV1UpdateSiteMutation,
  useWebApiV1ListSitesQuery,
  useWebApiV1DeleteSiteMutation,
  useWebApiV1CreateOarMutation,
  useWebApiV1UpdateOarMutation,
  useWebApiV1ListOarsQuery,
  useWebApiV1DeleteOarMutation,
  SiteOut,
  OrganAtRiskIn,
  useProtocolApiV1ListDataQuery,
  useProtocolApiV1TreatmentSiteDataQuery,
  useProtocolApiV1OrganDataQuery,
  OrganAtRiskOut,
} from "@providers/hop-ord-server/api";
import OarTable, { OarInfo } from "@components/OarTable/OarTable";
import {
  areDoseValuesValid,
  isNumeric,
  isSpecifyMandatory,
  parseListData,
} from "@utils";
import { ListDataCategory } from "@enums";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { TextOverflowTooltip } from "@components";
import React from "react";
import SiteGroupPanelSkeleton from "./SiteGroupPanelSkeleton";
import { SiteGroupOut } from "@providers/hop-ord-server/api";
import { getDoses } from "@utils/doseAndFractions";
import SiteTable from "@components/SiteTable/SiteTable";

const removeInvalidSiteData = (
  site: DosePrescription,
  listOptions: { [k: string]: string[] },
  isReadOnly: boolean,
): DosePrescription => {
  const modifiedSite = { ...site };
  const errors: string[] = [];
  const touched: string[] = [];

  const checkOpts = (optName: string, value: string, altName?: string) => {
    if (
      !isReadOnly &&
      (value === "" ||
        !listOptions[altName ?? optName] ||
        !listOptions[altName ?? optName].includes(value))
    ) {
      errors.push(optName);
      return "";
    }
    return value;
  };

  modifiedSite.name = checkOpts("name", site.name, "treatmentSite");
  if (site.name && isSpecifyMandatory(site.name || "") && !site.nameSpecify) {
    errors.push("name");
  }

  modifiedSite.laterality = checkOpts("laterality", site.laterality);
  if (modifiedSite.laterality === "") {
    errors.push("laterality");
  }
  modifiedSite.technique = checkOpts("technique", site.technique);
  if (modifiedSite.technique === "") {
    errors.push("technique");
  }

  // other stuff
  if (
    (site.fractions !== null && !isNumeric(site.fractions)) ||
    site.fractions === null
  ) {
    modifiedSite.fractions = null;
    errors.push("fractions");
  }
  if (site.highDose === null || !isNumeric(site.highDose)) {
    modifiedSite.highDose = null;
    errors.push("dose");
  }
  if (site.lowDose !== null && !isNumeric(site.lowDose)) {
    modifiedSite.lowDose = null;
    errors.push("dose");
  }
  if (site.mid1Dose !== null && !isNumeric(site.mid1Dose)) {
    modifiedSite.mid1Dose = null;
    errors.push("dose");
  }
  if (site.mid2Dose !== null && !isNumeric(site.mid2Dose)) {
    modifiedSite.mid2Dose = null;
    errors.push("dose");
  }
  const isDoseValid = areDoseValuesValid(
    site.highDose || null,
    site.mid1Dose || null,
    site.mid2Dose || null,
    site.lowDose || null,
  );
  if (!isDoseValid) {
    errors.push("dose");
  }
  modifiedSite.errors = errors;
  modifiedSite.touched = modifiedSite.touched || touched;
  return modifiedSite;
};

const removeInvalidOarData = (oarData: OarInfo) => {
  const modifiedOarData = { ...oarData };
  const errors: string[] = [];
  const touched: string[] = [];
  modifiedOarData.errors = errors;
  modifiedOarData.touched = modifiedOarData.touched || touched;
  return modifiedOarData;
};

export const siteGroupName = (
  dosePrescriptions: SiteGroupName[],
  doseUnit: string,
  fractionUnit: string,
): string => {
  const siteNames = dosePrescriptions.map((site) => {
    const doses = getDoses(site).join("-");
    if (!site.name) return "";
    const specifyLabel = site.nameSpecify ? ` ${site.nameSpecify}` : "";
    return `${site.name}${specifyLabel} ${site.technique} ${doses}${doseUnit}/${
      site.fractions || ""
    }${fractionUnit}`;
  });
  const siteName = siteNames.join(" + ");
  return siteName;
};

export interface SiteGroupPanelRefObject {
  trigger: () => boolean;
}

const categories = [
  ListDataCategory.TREATMENT_SITE,
  ListDataCategory.ISODOSE,
  ListDataCategory.LATERALITY,
  ListDataCategory.TECHNIQUE,
  ListDataCategory.SEQUENCE,
  ListDataCategory.FREQUENCY,
  ListDataCategory.ENERGY,
  ListDataCategory.TREATING_DEPARTMENT,
];

export interface SiteGroupPanelProps {
  index: number;
  orderId: number;
  tenantId: number;
  isReadOnly: boolean;
  siteGroup: SiteGroupOut;
  children?: React.ReactNode;
  submitCount?: number;
  onDeleteCallback: (siteGroup: SiteGroupOut) => void;
  reportValidity: (id: number | string, isValid: boolean) => void;
  doseUnit: string;
  fractionUnit: string;
  isImported: boolean;
}

const SiteGroupPanel = ({
  reportValidity,
  submitCount,
  index,
  isReadOnly,
  orderId,
  tenantId,
  siteGroup,
  onDeleteCallback,
  doseUnit,
  fractionUnit,
  isImported,
}: SiteGroupPanelProps) => {
  const siteGroupId = (siteGroup.id as number) || 0;
  const [dosePrescriptions, setDosePrescriptions] = useState<
    DosePrescription[]
  >([]);
  const [isSiteGroupValid, setIsSiteGroupValid] = useState<boolean>(false);
  const [oars, setOars] = useState<OarInfo[]>([]);
  const [addOar] = useWebApiV1CreateOarMutation();
  const [updateOar] = useWebApiV1UpdateOarMutation();
  const [deleteOar] = useWebApiV1DeleteOarMutation();
  const [addSite] = useWebApiV1CreateSiteMutation();
  const [updateSite] = useWebApiV1UpdateSiteMutation();
  const [deleteSite] = useWebApiV1DeleteSiteMutation();
  const { data, isLoading } = useWebApiV1ListSitesQuery({
    orderId: orderId.toString(),
    siteGroupId: siteGroupId.toString(),
  });
  const { data: oarData, isLoading: oarIsLoading } = useWebApiV1ListOarsQuery({
    orderId: orderId.toString(),
    siteGroupId: siteGroupId.toString(),
  });

  const { data: treatmentSiteData } = useProtocolApiV1TreatmentSiteDataQuery({
    tenantId,
  });
  const { data: organData } = useProtocolApiV1OrganDataQuery({
    tenantId,
  });

  /** List Data query */
  const { data: listData, isLoading: listDataLoading } =
    useProtocolApiV1ListDataQuery({
      categories,
      tenantIds: [tenantId],
    });

  const listOptions = useMemo(() => {
    const options = parseListData(listData || [], categories);
    options[ListDataCategory.TREATMENT_SITE] =
      treatmentSiteData?.map((site) => site.name) || [];
    options[ListDataCategory.ORGAN] =
      organData?.map((organ) => organ.name) || [];
    return options;
  }, [listData, treatmentSiteData, organData]);

  const treatmentSiteOptions = listOptions[ListDataCategory.TREATMENT_SITE];
  const techniqueOptions = listOptions[ListDataCategory.TECHNIQUE];
  const isodoseOptions = listOptions[ListDataCategory.ISODOSE];
  const lateralityOptions = listOptions[ListDataCategory.LATERALITY];
  const sequenceOptions = listOptions[ListDataCategory.SEQUENCE];
  const frequencyOptions = listOptions[ListDataCategory.FREQUENCY];
  const energyOptions = listOptions[ListDataCategory.ENERGY];
  const organOptions = listOptions[ListDataCategory.ORGAN];

  useEffect(() => {
    if (oarData) {
      setOars(oarData.map((oar) => removeInvalidOarData(oar)));
    }
  }, [oarData]);

  // TEMP validation passed back to the CareplanPage
  const [oarsAreValid, setOarsAreValid] = useState<boolean>(false);

  useEffect(() => {
    reportValidity(siteGroupId, isSiteGroupValid);
  }, [isSiteGroupValid, reportValidity, siteGroupId]);

  const validateSiteGroup = useCallback(async () => {
    const isDosePrescriptionsValid = dosePrescriptions.every(
      (site) => site.errors && site.errors.length === 0,
    );
    const siteGroupIsValid =
      dosePrescriptions.length > 0 && isDosePrescriptionsValid && oarsAreValid;
    setIsSiteGroupValid(siteGroupIsValid);
  }, [dosePrescriptions, oarsAreValid]);

  useEffect(() => {
    validateSiteGroup();
  }, [dosePrescriptions, oarsAreValid, submitCount, validateSiteGroup]);

  useEffect(() => {
    if (submitCount && submitCount > 0) {
      // update the table to touch all the fields
      setDosePrescriptions((prescription) => {
        return prescription.map((site) => {
          return {
            ...site,
            touched: [
              "name",
              "laterality",
              "technique",
              "sequence",
              "isodose",
              "frequency",
              "energy",
              "fractions",
              "dose",
            ],
          };
        });
      });
      setOars((oars) => {
        return oars.map((oar) => ({ ...oar, touched: ["organ"] }));
      });
    }
  }, [submitCount]);

  useEffect(() => {
    // if one of the list data category is empty then it won't show any data in table :(
    if (data) {
      //&& Object.values(listOptions).every((v) => v.length > 0)) {
      // keep the doseprescription fields that are touched.
      setDosePrescriptions((prevRows) => {
        return data.map((site) => {
          const newSite = removeInvalidSiteData(
            site as DosePrescription,
            listOptions,
            isReadOnly,
          );
          const prevRow = prevRows.find((row) => row.id === site.id);
          if (prevRow) {
            newSite.touched = prevRow.touched;
          }
          return newSite;
        });
      });
    }
  }, [data, isReadOnly, listOptions]);

  if (isLoading || oarIsLoading || listDataLoading) {
    return <SiteGroupPanelSkeleton />;
  }

  return (
    <Stack
      key={siteGroupId}
      id={`site-group-panel-${index}`}
      sx={{ scrollMarginTop: "17rem" }}
    >
      <Stack
        direction="row"
        sx={{
          width: 1,
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <TextOverflowTooltip sx={{ paddingY: 2 }} variant="h6">
          {siteGroupName(
            dosePrescriptions,
            doseUnit || "",
            fractionUnit || "",
          ) || `Site Group ${index + 1}`}
        </TextOverflowTooltip>

        {!isReadOnly && !isImported && (
          <IconButton
            onClick={() => onDeleteCallback(siteGroup)}
            data-testid={`delete-site-group-${siteGroupId}`}
            style={{ marginLeft: 4 }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>
      <Stack spacing={2}>
        <SiteTable
          dataTestid={`site-table-${index}`}
          isReadOnly={isReadOnly}
          dosePrescriptions={dosePrescriptions}
          treatmentSiteOptions={treatmentSiteOptions}
          lateralityOptions={lateralityOptions}
          techniqueOptions={techniqueOptions}
          sequenceOptions={sequenceOptions}
          isodoseOptions={isodoseOptions}
          frequencyOptions={frequencyOptions}
          energyOptions={energyOptions}
          doseUnit={doseUnit || ""}
          fractionUnit={fractionUnit || ""}
          isImported={isImported}
          addSite={(name, nameSpecify) => {
            addSite({
              siteGroupId: siteGroupId,
              manualSiteName: name,
              manualSiteSpecify: nameSpecify,
            }).then((res) => {
              const siteData = (res as unknown as { data: SiteOut }).data;
              const newRow: DosePrescription = {
                ...siteData,
                errors: [],
                touched: [],
              };
              setDosePrescriptions((prevRows) => [...prevRows, newRow]);
            });
          }}
          removeSite={(id: string | number): void => {
            deleteSite({ siteId: id as number });
            setDosePrescriptions((prevRows) =>
              prevRows.filter((row) => row.id !== id),
            );
          }}
          updateSite={(site: DosePrescription): boolean => {
            const modifiedSite = removeInvalidSiteData(
              site as DosePrescription,
              listOptions,
              isReadOnly,
            );
            updateSite({
              siteId: site.id as number,
              siteOut: modifiedSite as SiteOut,
            });
            setDosePrescriptions((prevRows) =>
              prevRows.map((row) => (row.id === site.id ? modifiedSite : row)),
            );
            return true;
          }}
          submitCount={submitCount || 0}
        />
        <OarTable
          dataTestId={`oar-table-${siteGroupId}`}
          isReadOnly={isReadOnly}
          isImported={isImported}
          oarData={oars}
          updateOar={(oar: OrganAtRiskIn): boolean => {
            updateOar({
              oarId: oar.id as number,
              organAtRiskIn: oar as OrganAtRiskIn,
            });
            setOars((prevRows) =>
              prevRows.map((row) =>
                row.id === oar.id ? (oar as OrganAtRiskOut) : row,
              ),
            );
            return true;
          }}
          removeOar={(id: string | number): void => {
            deleteOar({
              oarId: id as number,
            });
            setOars((prevRows) => prevRows.filter((row) => row.id !== id));
          }}
          addOar={() => {
            addOar({ siteGroupId }).then((res) => {
              const oarData = (res as unknown as { data: OrganAtRiskOut }).data;
              const newRow: OrganAtRiskOut = {
                ...oarData,
              };
              setOars((prevRows) => [...prevRows, newRow]);
            });
          }}
          organOptions={organOptions}
          submitCount={submitCount}
          reportValidity={setOarsAreValid}
          doseUnit={doseUnit}
        />
      </Stack>
    </Stack>
  );
};

export default SiteGroupPanel;
